import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface ValidationStatusT {
    validation_id: string | null;
    is_valid: boolean | null;
    is_stale: boolean | null;
    validated_at: string | null;
}

/** Keyed by form_id, for a staleness/validated badge column in the forms builder list. */
export async function selectFormValidationStatuses(): Promise<Record<string, ValidationStatusT>> {
    const { data, error } = await supabaseClient
        .from("form_validation_status")
        .select("form_id, validation_id, is_valid, is_stale, validated_at");
    if (error) throw error;

    const byFormId: Record<string, ValidationStatusT> = {};
    for (const row of data ?? []) {
        if (!row.form_id) continue;
        byFormId[row.form_id] = {
            validation_id: row.validation_id,
            is_valid: row.is_valid,
            is_stale: row.is_stale,
            validated_at: row.validated_at,
        };
    }
    return byFormId;
}

export const formValidationStatusesQueryOptions = () =>
    queryOptions({
        queryKey: ["validations", "form-status"],
        queryFn: () => selectFormValidationStatuses(),
    });

export function useFormValidationStatusesQuery() {
    return useQuery(formValidationStatusesQueryOptions());
}

/** Keyed by schedule_id, for the equivalent column in the schedules builder list. */
export async function selectScheduleValidationStatuses(): Promise<Record<string, ValidationStatusT>> {
    const { data, error } = await supabaseClient
        .from("schedule_validation_status")
        .select("schedule_id, validation_id, is_valid, is_stale, validated_at");
    if (error) throw error;

    const byScheduleId: Record<string, ValidationStatusT> = {};
    for (const row of data ?? []) {
        if (!row.schedule_id) continue;
        byScheduleId[row.schedule_id] = {
            validation_id: row.validation_id,
            is_valid: row.is_valid,
            is_stale: row.is_stale,
            validated_at: row.validated_at,
        };
    }
    return byScheduleId;
}

export const scheduleValidationStatusesQueryOptions = () =>
    queryOptions({
        queryKey: ["validations", "schedule-status"],
        queryFn: () => selectScheduleValidationStatuses(),
    });

export function useScheduleValidationStatusesQuery() {
    return useQuery(scheduleValidationStatusesQueryOptions());
}
